import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ITEMS } from './constants';
import { useMapState, useUIState } from './contexts/GameStateManager';
import { useToast } from './hooks/useToast';

const ResourceNodeMenu: React.FC = () => {
  const mapState = useMapState();
  const { triggerInventoryPulse } = useUIState();
  const { showError } = useToast();
  const mineButtonRef = useRef<HTMLButtonElement>(null);
  const flashTimeoutRef = useRef<number | null>(null);
  const flashRafRef = useRef<number | null>(null);
  const [isMineButtonFlashing, setIsMineButtonFlashing] = useState(false);

  const menuState = mapState.resourceNodeMenu;

  const resourceInfo = useMemo(() => {
    if (!menuState) {
      return null;
    }

    const resourceItem = Object.values(ITEMS).find(
      item => item.type === menuState.resourceItemType
    );

    return {
      name: resourceItem?.name ?? menuState.resourceItemType,
      character: resourceItem?.character ?? menuState.resourceCharacter,
    };
  }, [menuState]);

  useEffect(() => {
    if (menuState) {
      mineButtonRef.current?.focus();
    }
  }, [menuState]);

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current !== null) {
        window.clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = null;
      }

      if (flashRafRef.current !== null) {
        window.cancelAnimationFrame(flashRafRef.current);
        flashRafRef.current = null;
      }
    };
  }, []);

  if (!menuState || !resourceInfo) {
    return null;
  }

  const handleMine = (): void => {
    if (flashTimeoutRef.current !== null) {
      window.clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = null;
    }

    if (flashRafRef.current !== null) {
      window.cancelAnimationFrame(flashRafRef.current);
      flashRafRef.current = null;
    }

    setIsMineButtonFlashing(false);

    flashRafRef.current = window.requestAnimationFrame(() => {
      setIsMineButtonFlashing(true);
      flashRafRef.current = null;

      flashTimeoutRef.current = window.setTimeout(() => {
        setIsMineButtonFlashing(false);
        flashTimeoutRef.current = null;
      }, 550);
    });

    const result = mapState.mineResourceNode(menuState.position);

    if (result.success && result.added && result.itemType) {
      triggerInventoryPulse();
      return;
    }

    if (result.reason === 'inventory-full') {
      showError('Inventory is full. Clear space before mining more resources.');
      return;
    }

    showError('Unable to mine this resource right now.');
  };

  const handleClose = (): void => {
    mapState.closeResourceNodeMenu();
  };

  return (
    <div className='resource-node-menu' role='dialog' aria-modal='true'>
      <div className='resource-node-menu__header'>
        <h3>Manual Mining</h3>
        <button
          className='close-button'
          onClick={handleClose}
          aria-label='Close mining menu'
        >
          ✕
        </button>
      </div>
      <div className='resource-node-menu__content'>
        <p className='resource-node-menu__title'>
          {resourceInfo.character} {resourceInfo.name}
        </p>
        <button
          ref={mineButtonRef}
          className={`resource-node-menu__mine-button${
            isMineButtonFlashing
              ? ' resource-node-menu__mine-button--flashing'
              : ''
          }`}
          onClick={handleMine}
        >
          Mine 1× {resourceInfo.name}
        </button>
        <p className='resource-node-menu__hint'>
          Tip: Build a miner for continuous extraction once you can afford it.
        </p>
      </div>
    </div>
  );
};

export default ResourceNodeMenu;
